import {
  WidgetCreateActionUtil,
  WidgetPatchActionUtil,
  WidgetLockActionUtil,
  WidgetSyncActionUtil,
} from "../actions/WidgetActionUtil";
import { WidgetsPartUtil } from "../parts/WidgetsPartUtil";
import type { AgentAction } from "../../shared/types/AgentAction";
import type { PromptPart } from "../../shared/types/PromptPart";

// Import action utils to ensure they register themselves
import { CloseActionUtil } from "../actions/CloseActionUtil";
import { EndLessonActionUtil } from "../actions/EndLessonActionUtil";
import { NextSlideActionUtil } from "../actions/NextSlideActionUtil";
import { CreateActionUtil } from "../actions/CreateActionUtil";
import { DeleteActionUtil } from "../actions/DeleteActionUtil";
import { GestureActionUtil } from "../actions/GestureActionUtil";
import { LabelActionUtil } from "../actions/LabelActionUtil";
import { MessageActionUtil } from "../actions/MessageActionUtil";
import { MoveActionUtil } from "../actions/MoveActionUtil";
import { HighlightActionUtil } from "../actions/HighlightActionUtil";
import { PenActionUtil } from "../actions/PenActionUtil";
import { UnknownActionUtil } from "../actions/UnknownActionUtil";
import { UpdateActionUtil } from "../actions/UpdateActionUtil";

// Import prompt part utils to ensure they register themselves
import { AgentViewportBoundsPartUtil } from "../parts/AgentViewportBoundsPartUtil";
import { BlurryShapesPartUtil } from "../parts/BlurryShapesPartUtil";
import { InkClustersPartUtil } from "../parts/InkClustersPartUtil";
import { InkCropsPartUtil } from "../parts/InkCropsPartUtil";
import { MessagesPartUtil } from "../parts/MessagesPartUtil";
import { ModePartUtil } from "../parts/ModePartUtil";
import { PeripheralShapesPartUtil } from "../parts/PeripheralShapesPartUtil";
import { ScreenshotPartUtil } from "../parts/ScreenshotPartUtil";
import { SelectedShapesPartUtil } from "../parts/SelectedShapesPartUtil";
import { TimePartUtil } from "../parts/TimePartUtil";
import { UserViewportBoundsPartUtil } from "../parts/UserViewportBoundsPartUtil";

/**
 * What an agent can see and do when in a given mode.
 *
 * This is a discriminated union based on the `active` property:
 * - Active modes can take actions and receive prompt parts
 * - Inactive modes cannot take actions (agent is idle/waiting)
 */
export type AgentModeDefinition = {
  /** A unique identifier for the agent mode. */
  type: string;
} & (
  | {
      /** Whether the agent is active in this mode and can take actions. */
      active: true;
      /** The prompt parts that determine what information will be sent to the model. */
      parts: PromptPart["type"][];
      /** The actions that the agent can take. */
      actions: AgentAction["_type"][];
    }
  | {
      /** Whether the agent is active in this mode and can take actions. */
      active: false;
    }
);

/**
 * All agent mode definitions.
 *
 * To add a new mode, add a new object to this array.
 * To change what the agent can see, edit the `parts` array.
 * To change what the agent can do, edit the `actions` array.
 *
 * By importing utils directly, we ensure they are registered when this module is loaded.
 */
export const AGENT_MODE_DEFINITIONS = [
  {
    type: "idling",
    active: false,
  },
  {
    type: "working",
    active: true,

    /**
     * Prompt parts determine what information will be sent to the model.
     */
    parts: [
      // Mode (the gateway requires it)
      ModePartUtil.type,

      // Request
      MessagesPartUtil.type,

      // Viewport
      ScreenshotPartUtil.type,
      UserViewportBoundsPartUtil.type,
      AgentViewportBoundsPartUtil.type,

      // Shapes
      BlurryShapesPartUtil.type,
      WidgetsPartUtil.type,
      InkClustersPartUtil.type,
      InkCropsPartUtil.type,
      PeripheralShapesPartUtil.type,
      SelectedShapesPartUtil.type,

      // History

      // Metadata
      TimePartUtil.type,
    ],

    /**
     * Agent actions determine what actions the agent can take.
     */
    actions: [
      // Communication
      MessageActionUtil.type,

      // Individual shapes
      CreateActionUtil.type,
      WidgetCreateActionUtil.type,
      WidgetPatchActionUtil.type,
      WidgetLockActionUtil.type,
      WidgetSyncActionUtil.type,
      DeleteActionUtil.type,
      UpdateActionUtil.type,
      LabelActionUtil.type,
      MoveActionUtil.type,

      // Canvas lifecycle
      CloseActionUtil.type,
      NextSlideActionUtil.type,
      EndLessonActionUtil.type,

      // Drawing
      PenActionUtil.type,
      HighlightActionUtil.type,

      // Transient pointer marks (overlay-only, never enter the shape store)
      GestureActionUtil.type,

      // Internal (required)
      UnknownActionUtil.type,
    ],
  },
] as const satisfies AgentModeDefinition[];

/**
 * An agent mode definition from the AGENT_MODE_DEFINITIONS array.
 */
export type AgentModeDefinitionType = (typeof AGENT_MODE_DEFINITIONS)[number];

/**
 * The type of an agent mode.
 */
export type AgentModeType = AgentModeDefinitionType["type"];

/**
 * Get the mode definition for a given mode type.
 * @param type - The mode type to look up.
 * @returns The mode definition.
 */
export function getAgentModeDefinition(
  type: AgentModeType,
): AgentModeDefinitionType {
  const mode = AGENT_MODE_DEFINITIONS.find((m) => m.type === type);
  if (!mode) throw new Error(`Unknown agent mode: ${type}`);
  return mode;
}
